import { DataEndpoint } from "../../../lib/data/remote/DataEndpoint.js";
import { Services } from "../../../lib/services/Services.js";
import { generateUid } from "../../../lib/utils/uid.js";
import { Component } from "../../../lib/ui/Component.js";

class FileUploader {

    static STATUS_ERROR = -1;
    static STATUS_SUCCESS = 1;
    static STATUS_PREPARED = 10;
    static STATUS_UPLOADING = 20;
    static STATUS_UPLOADED = 21;
    static STATUS_MERGING = 30;
    static STATUS_MERGED = 31;

    constructor(physicalFile, options) {

        const {
            tempName = generateUid(),
            uploadFragmentPath = null,
            mergeFragmentsPath = null,
            authManager = null,
            folderUid = null,
            onSuccess = (uploader) => { },
            onProgress = (uploader, fragment) => { },
            onError = (uploader) => { },
            beforeSuccess = (uploader) => { },
            beforeProgress = (uploader, fragment) => { },
            beforeError = (uploader) => { }
        } = options || {};

        const config = Services.get('config');

        this.FRAGMENT_SIZE = (config.uploads && config.uploads.fragmentSize) || 512 * 1024;

        this.tempName = tempName;
        this.uploadFragmentPath = uploadFragmentPath;
        this.mergeFragmentsPath = mergeFragmentsPath;
        this.authManager = authManager;
        this.folderUid = folderUid;
        this.onSuccess = onSuccess;
        this.onProgress = onProgress;
        this.onError = onError;
        this.beforeSuccess = beforeSuccess;
        this.beforeProgress = beforeProgress;
        this.beforeError = beforeError;

        this.uploadFragmentEndpoint = new DataEndpoint(uploadFragmentPath, authManager);
        this.mergeFragmentsEndpoint = new DataEndpoint(mergeFragmentsPath, authManager);

        this.fileName = physicalFile.name;
        this.fileSize = physicalFile.size;
        this.fragments = this.createFragments(physicalFile);
        this.uploadedSize = 0;
        this.status = FileUploader.STATUS_PREPARED;

    };

    createFragments(physicalFile) {

        let size = physicalFile.size,
            start = 0,
            end = Math.min(size, this.FRAGMENT_SIZE),
            index = 0;

        let fragments = [];

        while (start < physicalFile.size) {
            let blob = physicalFile.slice(start, end);
            fragments.push({
                fileName: this.fileName,
                tempName: this.tempName,
                index: index,
                size: end - start,
                blob: blob,
                status: FileUploader.STATUS_PREPARED,
            });
            start = end;
            end = Math.min(start + this.FRAGMENT_SIZE, size);
            index++;
        }

        return fragments;

    };

    updateProgress(fragment) {

        this.uploadedSize = this.fragments.reduce((result, fragment) => {

            if (fragment.status == FileUploader.STATUS_UPLOADED)
                result += fragment.size;
            return result;

        }, 0);

        this.beforeProgress(this, fragment);

    };

    async uploadFragments() {

        let fragmentPromises = this.fragments.map(async (fragment) => {

            fragment.status = FileUploader.STATUS_UPLOADING;

            let blobBase64 = await blobToBase64(fragment.blob)

            let data = {
                fileName: fragment.fileName,
                tempName: fragment.tempName,
                index: fragment.index,
                size: fragment.size,
                data: blobBase64,
            };

            console.log(`Uploading fragment ${fragment.index} of ${fragment.tempName}...`);

            try {

                await this.uploadFragmentEndpoint.post('', data);

                fragment.status = FileUploader.STATUS_UPLOADED;
                console.log(`Fragment ${fragment.index} of ${fragment.tempName} uploaded.`);
                this.updateProgress(fragment);
                this.onProgress(this, fragment);

            } catch (error) {

                fragment.status = FileUploader.STATUS_ERROR;
                this.beforeProgress(this, fragment);
                this.onProgress(this, fragment);

            }

            return fragment;

        });

        return Promise.all(fragmentPromises);

        // ------------

        function blobToBase64(blob) {

            return new Promise((resolve, reject) => {

                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => resolve(reader.result.split(",")[1]); // Odebereme "data:mime/type;base64,"
                reader.onerror = reject;

            });

        }

    };

    async mergeFragments() {

        let mergeParams = {
            fileName: this.fileName,
            tempName: this.tempName,
            fileSize: this.fileSize,
            folderUid: this.folderUid,
        };

        const result = await this.mergeFragmentsEndpoint.post('', mergeParams);

        return result;

    };

    async upload() {

        const i18n = Services.get('i18n');

        this.status = FileUploader.STATUS_UPLOADING;
        this.statusMessage = i18n.t('component.upload_manager.status_uploading');

        let uploadedFragments = [];

        try {

            uploadedFragments = await this.uploadFragments();

        } catch (error) {

            this.status = FileUploader.STATUS_ERROR;
            this.statusMessage = i18n.t('component.upload_manager.err_fragments_upload') + ': '
                + ((error && error.message) || error || '?');
            this.beforeError(this);
            this.onError(this);
            return false;

        }

        if (this.fragments.some(fragment => fragment.status == FileUploader.STATUS_ERROR || fragment.status == FileUploader.STATUS_UPLOADING)) {

            this.status = FileUploader.STATUS_ERROR;
            this.statusMessage = i18n.t('component.upload_manager.err_fragments_merge') + ': '
                + i18n.t('component.upload_manager.err_fragments_not_all_uploaded');
            this.beforeError(this);
            this.onError(this);
            return false;

        }

        this.status = FileUploader.STATUS_MERGING;
        this.statusMessage = i18n.t('component.upload_manager.status_merging');

        try {

            await this.mergeFragments();

        } catch (error) {

            this.status = FileUploader.STATUS_ERROR;
            this.statusMessage = i18n.t('component.upload_manager.err_fragments_merge') + ': '
                + ((error && error.message) || error || '?');
            this.beforeError(this);
            this.onError(this);
            return false;

        }

        this.status = FileUploader.STATUS_SUCCESS;
        this.statusMessage = i18n.t('component.upload_manager.status_uploaded');
        this.beforeSuccess(this);
        this.onSuccess(this);
        return true;


    }

}

// --------------------------------------------------------------------------------------------

export class UploadPanel extends Component {

    static RENDER_FULL = 'full';
    static RENDER_MINIFIED = 'minified';

    constructor(parent, options = {}) {

        super(parent, options);

        const {
            uploadFragmentPath = null,
            mergeFragmentsPath = null,
            authManager = this.getApp?.()?.getAuthManager?.() || null,
        } = options;

        if (!uploadFragmentPath) throw new Error('[UploadPanel] "uploadFragmentPath" is required.');
        if (!mergeFragmentsPath) throw new Error('[UploadPanel] "mergeFragmentsPath" is required.');

        this.uploadFragmentPath = uploadFragmentPath;
        this.mergeFragmentsPath = mergeFragmentsPath;
        this.authManager = authManager;
        this.uploaders = [];

        this.visible = false;
        this.renderMode = UploadPanel.RENDER_FULL;

        return this;

    };

    createUploader(physicalFile, options = {}) {
        return new FileUploader(physicalFile, options);
    }

    uploadFile(physicalFile, options = {}) {

        options = Object.assign({
            tempName: generateUid(),
            fileName: physicalFile.name,
            fileSize: physicalFile.size,
            folderUid: null,
            uploadFragmentPath: this.uploadFragmentPath,
            mergeFragmentsPath: this.mergeFragmentsPath,
            authManager: this.authManager,
            onProgress: (uploader, fragment) => { },
            onSuccess: (uploader) => { },
            onError: (uploader) => { },
        }, options || {});

        options.beforeProgress = (uploader, fragment) => {
            this.fileUploadProgressed(uploader, fragment);
        };
        options.beforeSuccess = (uploader) => {
            this.fileUploadFinished(uploader);
        };
        options.beforeError = (uploader) => {
            this.fileUploadFailed(uploader);
        };

        let uploader = this.createUploader(physicalFile, options);

        this.uploaders.push(uploader);
        this.visible = true;
        this.invalidate();

        uploader.upload();

    };

    fileUploadProgressed(uploader, fragment) {

        this.invalidate();

    };

    fileUploadFinished(uploader) {

        this.invalidate();

        setTimeout(() => {

            var index = this.uploaders.indexOf(uploader);
            if (index >= 0) {
                this.uploaders.splice(index, 1);
            }
            this.visible = this.uploaders.length > 0;
            this.invalidate();

        }, 1000);

    };

    fileUploadFailed(uploader) {

        this.invalidate();

        setTimeout(() => {

            var index = this.uploaders.indexOf(uploader);
            if (index >= 0) {
                this.uploaders.splice(index, 1);
            }
            this.invalidate();

        }, 10000);

    };

    toggleRenderMode() {

        this.renderMode = this.renderMode == this.RENDER_FULL ? this.RENDER_MINIFIED : this.RENDER_FULL;
        this.invalidate();

    };

    _render(vm) {

        const ct = Services.get('templates');
        const manager = this;

        let uploaders = (manager.uploaders || []);

        // uploaders = [
        //     { status: -1, fileName: 'Test-1.txt', fileSize: 100, uploadedSize: 50, tempName: 'alfa' },
        //     { status: 1, fileName: 'Test-2.txt', fileSize: 200, uploadedSize: 50, tempName: 'beta' },
        //     { status: 1, fileName: 'Test-3.txt', fileSize: 300, uploadedSize: 70, tempName: 'gama' },
        //     { status: 1, fileName: 'Test-4.txt', fileSize: 400, uploadedSize: 90, tempName: 'delta' },
        //     { status: 1, fileName: 'Test-5.txt', fileSize: 500, uploadedSize: 110, tempName: 'epsilon' },
        // ];

        let items = uploaders.map((uploader, index) => {

            let css = (uploader.status == uploader.STATUS_ERROR ? '.error' : (uploader.status == uploader.STATUS_SUCCESS ? '.success' : ''));

            return ct.el('.max.truncate' + css, { id: 'uploader-' + uploader.tempName, 'aria-live': 'polite' }, [
                ct.h3('.small.truncate', [uploader.fileName]),
                ct.el('progress.progress', { value: uploader.uploadedSize, min: 0, max: uploader.fileSize }),
            ]);

        });

        // items = [
        //     ct.el('.uploader.max.truncate', { id: 'uploader-123456790', 'aria-live': 'polite' }, [
        //         ct.el('h6.small.truncate', [
        //             'Very very very long file name 1234567890-1234567890-1234567890-1234567890.txt',
        //         ]),
        //         ct.el('progress.upload-progress', { value: 73, max: 100 }),
        //     ]),
        //     ct.el('.uploader.max.truncate', { id: 'uploader-123456790', 'aria-live': 'polite' }, [
        //         ct.el('h6.small.truncate', [
        //             'Very very very long file name 1234567890-1234567890-1234567890-1234567890.txt',
        //         ]),
        //         ct.el('progress.upload-progress', { value: 73, max: 100 }),
        //     ]),
        //     ct.el('.uploader.max.truncate', { id: 'uploader-123456790', 'aria-live': 'polite' }, [
        //         ct.el('h6.small.truncate', [
        //             'Very very very long file name 1234567890-1234567890-1234567890-1234567890.txt',
        //         ]),
        //         ct.el('progress.upload-progress', { value: 73, max: 100 }),
        //     ]),
        // ];

        let css = manager.visible ? '.active' : '',
            expanded = manager.renderMode == manager.RENDER_FULL,
            list = (expanded && items && items.length) ? ct.list('.border', items) : null;

        return ct.dialog('.upload-manager.top.narrow' + css, [
            ct.nav([
                ct.h3([
                    'Upload souborů'
                ]),
                ct.navgap(),
                ct.iconButton(expanded ? 'chevron-down' : 'chevron-down', '.transparent.circle', { onclick: (e) => { manager.toggleRenderMode(); } }),
            ]),
            list
        ]);

    };

}
